import React, { FC, useEffect, useRef } from 'react'
import {
  ScreenReaderOnly,
  Text,
  IconButton,
  Flex,
  Box,
} from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import {
  IconSystemChevronLeft,
  IconSystemChevronRight,
} from '@moonpig/launchpad-assets'
import { SortOrder } from '@moonpig/web-explore-types-graphql'
import { colorValue } from '@moonpig/launchpad-theme'
import { useFindLocaleText } from '../../../../text-localisation'
import { Panel as SlidingMenuPanel } from '../SlidingMenu'
import { SortOptions } from './SortOptions'
import { shouldShowClearButton } from '../../../../utils/GalleryFiltering/shouldShowClearButton'
import { ClearButton } from '../../../ClearButton'
import { FilterSource } from '../../../../types'
import { FILTERS_MENU_HEADER_HEIGHT_PX } from '../../constants'

const ICON_SIZE = 24

const StyledButton = styled.button.attrs(
  ({ isSubCategory }: { isSubCategory: boolean }) => ({
    className: isSubCategory && 'isSubCategory',
  }),
)<{ isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: ${FILTERS_MENU_HEADER_HEIGHT_PX};
  ${s({
    p: 6,
    borderBottom: 1,
    borderColor: 'colorBorder04',
    color: 'colorTextLabel',
  })}

  &:hover {
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
    })}
  }

  &:active {
    ${s({
      bgcolor: 'colorBackground03',
      borderColor: 'colorBlack10',
    })}
  }

  &:focus,
  &:focus-visible {
    outline-color: ${colorValue('colorInteractionFocusIndicator')};
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
      color: 'colorInteractionFocusIndicator',
    })}
  }
`

const StyledText = styled(Text)`
  ${s({
    typography: { xs: 'typeMobileDisplay05', md: 'typeDesktopBodyLabel' },
  })}
`

const StyledHeader = styled(Flex)<{ isOpen: boolean }>`
  position: sticky;
  z-index: 1051;

  ${({ isOpen }) =>
    s({
      top: isOpen ? -FILTERS_MENU_HEADER_HEIGHT_PX : 0,
      left: 0,
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      minHeight: FILTERS_MENU_HEADER_HEIGHT_PX,
      py: 3,
      pl: 3,
      pr: 6,
      borderBottom: 1,
      borderColor: 'colorBorder03',
      bgcolor: 'colorBackground01',
    })}
`

const StyledIconButton = styled(IconButton)`
  flex: 1;
`

type Props = {
  options: { label: string; value: string }[]
  value: string
  defaultValue: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  open: boolean
  resultsButton: JSX.Element
}

export const SortBy: FC<Props> = ({
  options,
  value,
  onChange,
  defaultValue,
  open,
  resultsButton,
}) => {
  const [isOpen, setIsOpen] = React.useState(open)
  const [showSortOptions, setShowSortOptions] = React.useState(open)
  const localiseText = useFindLocaleText()
  const sortByTitle = localiseText('find.sort_by')
  const backButton = useRef<HTMLButtonElement>(null)
  const selectedSortOption = options.find(
    option => option.value === value,
  )?.label
  const hideClearButton = !shouldShowClearButton({
    sortValue: value as SortOrder,
    defaultSortValue: defaultValue as SortOrder,
  })
  const toggleSortByMenu = () => setIsOpen(!isOpen)

  useEffect(() => {
    if (isOpen) {
      backButton.current?.focus()
    } else {
      setShowSortOptions(false)
    }
  }, [isOpen])

  return (
    <>
      <StyledButton
        data-testid="sort-by"
        onClick={() => {
          toggleSortByMenu()
          setShowSortOptions(true)
        }}
        aria-controls={'sort-by-filter-category-panel'}
        role="tab"
      >
        <Flex>
          <StyledText textAlign="left">{sortByTitle}</StyledText>
          <ScreenReaderOnly>
            {localiseText('find.filters_menu_panel.open', sortByTitle)}
          </ScreenReaderOnly>
        </Flex>
        <Flex color="colorInteractionSelectedState">
          <Text>{selectedSortOption}</Text>
          <IconSystemChevronRight width={ICON_SIZE} height={ICON_SIZE} />
        </Flex>
      </StyledButton>
      {showSortOptions && (
        <SlidingMenuPanel level={1} id={'sort-by-filter-category-panel'}>
          <StyledHeader isOpen={isOpen}>
            <StyledIconButton
              ref={backButton}
              icon={IconSystemChevronLeft}
              onClick={toggleSortByMenu}
              label={localiseText('find.filters_menu_panel.close', sortByTitle)}
              tabIndex={isOpen ? 0 : -1}
            />
            <StyledText flex={4} textAlign="center">
              {sortByTitle}
            </StyledText>
            <ClearButton
              level={1}
              type="sort"
              hide={hideClearButton}
              source={FilterSource.FiltersMenu}
            />
          </StyledHeader>
          <SortOptions
            options={options}
            value={value}
            onChange={onChange}
            disabled={!isOpen}
          />
          <Box position="absolute" bottom={0} width="100%">
            {resultsButton}
          </Box>
        </SlidingMenuPanel>
      )}
    </>
  )
}
